import { useEffect, useMemo, useRef, useState } from 'react';
import {
  makeStyles,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  IconButton,
  Typography,
  Tooltip,
  CircularProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  TextField,
  Checkbox,
  Divider,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import HistoryIcon from '@material-ui/icons/History';
import PublishIcon from '@material-ui/icons/Publish';
import RestoreIcon from '@material-ui/icons/Restore';
import SettingsIcon from '@material-ui/icons/Settings';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';
import InboxIcon from '@material-ui/icons/Inbox';
import LayersIcon from '@material-ui/icons/Layers';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CheckIcon from '@material-ui/icons/Check';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import DriveFileRenameOutlineIcon from '@material-ui/icons/Edit';
import FolderSpecialIcon from '@material-ui/icons/FolderSpecial';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import { useGenaiBuilderApi, Document, DocumentVersion } from './useGenaiBuilderApi';
import { useIsAdmin } from './useIsAdmin';

// ── Types ──────────────────────────────────────────────────────────────────

type FolderView =
  | { kind: 'all' }
  | { kind: 'folder'; name: string };

interface FolderNode {
  name: string;
  path: string;
  docCount: number;
  totalCount: number;
  children: FolderNode[];
  isPending: boolean;
}

interface CtxMenu {
  x: number;
  y: number;
  kind: 'file' | 'folder' | 'bg';
  doc?: Document;
  folderPath?: string;
}

type SortKey = 'name' | 'date' | 'type';
type SortDir = 'asc' | 'desc';

// ── Tree builder ───────────────────────────────────────────────────────────

function buildFolderTree(
  paths: string[],
  counts: Record<string, number>,
  pendingSet: Set<string>,
): FolderNode[] {
  const allPaths = new Set<string>();
  for (const p of paths) {
    const parts = p.split('/').filter(Boolean);
    for (let i = 1; i <= parts.length; i++) {
      allPaths.add(parts.slice(0, i).join('/'));
    }
  }
  const sorted = Array.from(allPaths).sort();
  const nodeMap = new Map<string, FolderNode>();
  const roots: FolderNode[] = [];

  for (const path of sorted) {
    const parts = path.split('/');
    const node: FolderNode = {
      name: parts[parts.length - 1],
      path,
      docCount: counts[path] ?? 0,
      totalCount: 0,
      children: [],
      isPending: pendingSet.has(path) && !counts[path],
    };
    nodeMap.set(path, node);
    if (parts.length === 1) {
      roots.push(node);
    } else {
      nodeMap.get(parts.slice(0, -1).join('/'))?.children.push(node);
    }
  }

  function calcTotal(n: FolderNode): number {
    n.totalCount = n.docCount + n.children.reduce((s, c) => s + calcTotal(c), 0);
    return n.totalCount;
  }
  roots.forEach(calcTotal);
  return roots;
}

function normalizeFolderPath(raw: string): string {
  return raw.split('/').map(s => s.trim()).filter(Boolean).join('/');
}

// ── Styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
    minHeight: 540,
  },
  // --- Sidebar ---
  sidebar: {
    width: 230,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 1.5, 0.5, 2),
  },
  sidebarTitle: {
    flex: 1,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
  },
  sidebarNewInput: { margin: theme.spacing(0.5, 1.5, 1) },
  navEntry: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.6, 1.5, 0.6, 2),
    cursor: 'pointer',
    userSelect: 'none' as const,
    borderLeft: '3px solid transparent',
    transition: 'background 0.1s, border-color 0.1s',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  navEntryActive: {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    '& $navLabel': { fontWeight: 600, color: theme.palette.primary.main },
  },
  navEntryDrop: {
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(63,81,181,0.25)' : 'rgba(63,81,181,0.12)',
    borderLeftColor: theme.palette.primary.main,
    outline: `2px dashed ${theme.palette.primary.light}`,
    outlineOffset: -2,
  },
  navIcon: { fontSize: 16, flexShrink: 0, color: theme.palette.text.secondary },
  navLabel: {
    flex: 1,
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  navLabelPending: { fontStyle: 'italic', opacity: 0.65 },
  navCount: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    background: theme.palette.action.hover,
    borderRadius: 10,
    padding: '1px 6px',
    minWidth: 20,
    textAlign: 'center' as const,
    flexShrink: 0,
  },
  expandArrow: { width: 18, flexShrink: 0, display: 'flex', alignItems: 'center' },
  // --- Main ---
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 44,
    flexShrink: 0,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flex: 1,
    overflow: 'hidden',
  },
  breadcrumbLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: 13,
    '&:hover': { textDecoration: 'underline' },
  },
  breadcrumbSep: { color: theme.palette.text.disabled, fontSize: 16 },
  breadcrumbCurrent: { fontWeight: 600, fontSize: 13 },
  tableScroll: {
    flex: 1,
    overflowY: 'auto' as const,
    position: 'relative' as const,
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center' as const,
    color: theme.palette.text.secondary,
  },
  // Folder rows in main table
  folderRow: {
    cursor: 'pointer',
    '&:hover $folderRowActions': { opacity: 1 },
  },
  folderRowDrop: {
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(63,81,181,0.2)' : 'rgba(63,81,181,0.08)',
    outline: `2px dashed ${theme.palette.primary.light}`,
    outlineOffset: -2,
  },
  folderRowActions: { opacity: 0, transition: 'opacity 0.15s' },
  folderName: { fontWeight: 500, fontSize: 13 },
  fileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    maxWidth: 260,
    fontSize: 13,
  },
  // Bulk action bar
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    borderTop: `1px solid ${theme.palette.primary.light}`,
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(63,81,181,0.15)' : 'rgba(63,81,181,0.06)',
    flexShrink: 0,
  },
  bulkCount: { fontWeight: 600, fontSize: 13, color: theme.palette.primary.main },
  uploadProgress: { padding: theme.spacing(0, 1.5, 0.75) },
  error: { color: theme.palette.error.main, padding: theme.spacing(0.5, 2), fontSize: 12 },
  supportedFormats: { padding: theme.spacing(0.75, 1.5), backgroundColor: theme.palette.action.hover, borderTop: `1px solid ${theme.palette.divider}` },
  moveChip: {
    cursor: 'pointer',
    margin: theme.spacing(0.25),
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
  },
  menuIcon: { minWidth: 32 },
}));

// ── Helpers ────────────────────────────────────────────────────────────────

const ACCEPTED_TYPES: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/vnd.oasis.opendocument.presentation': 'odp',
  'application/vnd.oasis.opendocument.spreadsheet': 'ods',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/rtf': 'rtf',
  'text/rtf': 'rtf',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'text/csv': 'csv',
  'application/json': 'json',
  'application/xml': 'xml',
  'text/xml': 'xml',
  'text/html': 'html',
  'text/x-sql': 'sql',
  'text/yaml': 'yaml',
};

const ACCEPTED_EXTENSIONS: Record<string, string> = {
  '.pdf': 'pdf', '.docx': 'docx', '.pptx': 'pptx', '.ppt': 'ppt',
  '.odt': 'odt', '.odp': 'odp', '.ods': 'ods', '.rtf': 'rtf',
  '.xlsx': 'xlsx', '.xls': 'xls', '.csv': 'csv',
  '.txt': 'txt', '.md': 'md', '.json': 'json', '.xml': 'xml',
  '.html': 'html', '.htm': 'htm', '.sql': 'sql', '.yaml': 'yaml',
  '.yml': 'yml', '.toml': 'toml', '.ini': 'ini', '.log': 'log',
};

const ACCEPT_ATTR = Object.keys(ACCEPTED_EXTENSIONS).join(',');

function getFileType(file: File): string | null {
  if (ACCEPTED_TYPES[file.type]) return ACCEPTED_TYPES[file.type];
  const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  return ACCEPTED_EXTENSIONS[ext] ?? null;
}



// ── FolderTreeNode (sidebar) ───────────────────────────────────────────────

interface TreeCtx {
  classes: ReturnType<typeof useStyles>;
  activeFolder: FolderView;
  onSelect: (v: FolderView) => void;
  expandedFolders: Set<string>;
  toggleExpand: (path: string) => void;
  dragOverFolder: string | null;
  onDragOver: (path: string, e: React.DragEvent) => void;
  onDragLeave: (path: string, e: React.DragEvent) => void;
  onDrop: (path: string, e: React.DragEvent) => void;
  onContextMenu: (e: React.MouseEvent, folderPath: string) => void;
}

function FolderTreeNode({ node, depth, ctx }: { node: FolderNode; depth: number; ctx: TreeCtx }) {
  const { classes } = ctx;
  const isActive = ctx.activeFolder.kind === 'folder' && ctx.activeFolder.name === node.path;
  const isDrop = ctx.dragOverFolder === node.path;
  const isExpanded = ctx.expandedFolders.has(node.path);

  return (
    <>
      <Box
        className={[classes.navEntry, isActive ? classes.navEntryActive : '', isDrop ? classes.navEntryDrop : ''].join(' ')}
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={() => ctx.onSelect({ kind: 'folder', name: node.path })}
        onContextMenu={e => ctx.onContextMenu(e, node.path)}
        onDragOver={e => ctx.onDragOver(node.path, e)}
        onDragLeave={e => ctx.onDragLeave(node.path, e)}
        onDrop={e => ctx.onDrop(node.path, e)}
      >
        <Box className={classes.expandArrow}>
          {node.children.length > 0 && (
            <IconButton size="small" style={{ padding: 1 }} onClick={e => { e.stopPropagation(); ctx.toggleExpand(node.path); }}>
              {isExpanded ? <ExpandMoreIcon style={{ fontSize: 14 }} /> : <ChevronRightIcon style={{ fontSize: 14 }} />}
            </IconButton>
          )}
        </Box>
        {isExpanded ? <FolderOpenIcon className={classes.navIcon} /> : <FolderIcon className={classes.navIcon} />}
        <span className={[classes.navLabel, node.isPending ? classes.navLabelPending : ''].join(' ')}>{node.name}</span>
        <span className={classes.navCount}>{node.totalCount}</span>
      </Box>
      {node.children.length > 0 && (
        <Collapse in={isExpanded} unmountOnExit>
          {node.children.map(c => <FolderTreeNode key={c.path} node={c} depth={depth + 1} ctx={ctx} />)}
        </Collapse>
      )}
    </>
  );
}

// ── DocumentLibrary ────────────────────────────────────────────────────────

export function DocumentLibrary() {
  const classes = useStyles();
  const api = useGenaiBuilderApi();
  const { isAdmin } = useIsAdmin();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastSelectedIdRef = useRef<string | null>(null);
  const draggingIdsRef = useRef<string[]>([]);
  const tableAreaRef = useRef<HTMLDivElement>(null);

  // Data
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  // Navigation
  const [activeFolder, setActiveFolder] = useState<FolderView>({ kind: 'all' });
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [pendingFolders, setPendingFolders] = useState<string[]>([]);

  // New folder
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderValue, setNewFolderValue] = useState('');

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Drag-and-drop
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);

  // Context menu
  const [ctxMenu, setCtxMenu] = useState<CtxMenu | null>(null);

  // Dialogs
  const [deletingDocs, setDeletingDocs] = useState<Document[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState<Document | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<string | null>(null);
  const [showRetentionDialog, setShowRetentionDialog] = useState<Document | null>(null);
  const [retentionValue, setRetentionValue] = useState('');
  const [savingRetention, setSavingRetention] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [movingDocs, setMovingDocs] = useState(false);

  // Rename folder dialog
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');
  const [renamingFolderBusy, setRenamingFolderBusy] = useState(false);

  // Remove folder dialog
  const [removingFolderPath, setRemovingFolderPath] = useState<string | null>(null);
  const [removingFolderBusy, setRemovingFolderBusy] = useState(false);

  // ── Derived ───────────────────────────────────────────────────────────────

  const folderCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const d of documents) {
      if (d.folder) c[d.folder] = (c[d.folder] ?? 0) + 1;
    }
    return c;
  }, [documents]);

  const allFolderPaths = useMemo(() => {
    const fromDocs = Array.from(new Set(documents.map(d => d.folder).filter(Boolean) as string[]));
    return Array.from(new Set([...fromDocs, ...pendingFolders]));
  }, [documents, pendingFolders]);

  const folderTree = useMemo(
    () => buildFolderTree(allFolderPaths, folderCounts, new Set(pendingFolders)),
    [allFolderPaths, folderCounts, pendingFolders],
  );

  // Flat map of path → totalCount for folder rows
  const folderTotalCounts = useMemo(() => {
    const m: Record<string, number> = {};
    function walk(nodes: FolderNode[]) {
      for (const n of nodes) { m[n.path] = n.totalCount; walk(n.children); }
    }
    walk(folderTree);
    return m;
  }, [folderTree]);

  // Sub-folders directly under the active folder
  const currentSubFolderPaths = useMemo(() => {
    const prefix = activeFolder.kind === 'folder' ? activeFolder.name + '/' : '';
    return Array.from(new Set(
      allFolderPaths
        .filter(p => p.startsWith(prefix) && !p.slice(prefix.length).includes('/'))
    )).sort();
  }, [allFolderPaths, activeFolder]);

  // Documents directly in the active folder (not in sub-folders)
  const currentDirectDocs = useMemo(() => {
    if (activeFolder.kind === 'all') return documents.filter(d => !d.folder);
    return documents.filter(d => d.folder === activeFolder.name);
  }, [documents, activeFolder]);

  const sortedDocs = useMemo(() => {
    return [...currentDirectDocs].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'date') cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      else if (sortKey === 'type') cmp = a.file_type.localeCompare(b.file_type);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [currentDirectDocs, sortKey, sortDir]);

  const allVisibleSelected =
    sortedDocs.length > 0 && sortedDocs.every(d => selectedIds.has(d.id));
  const someVisibleSelected =
    !allVisibleSelected && sortedDocs.some(d => selectedIds.has(d.id));

  const selectedDocs = useMemo(
    () => documents.filter(d => selectedIds.has(d.id)),
    [documents, selectedIds],
  );

  const flatFolderPaths = useMemo(() => [...allFolderPaths].sort(), [allFolderPaths]);

  const defaultUploadFolder = activeFolder.kind === 'folder' ? activeFolder.name : '';

  // ── Effects ───────────────────────────────────────────────────────────────

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      setDocuments(await api.listDocuments());
    } catch (err: any) {
      setError(err.message ?? 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocuments(); }, []);

  // Auto-expand ancestors in sidebar
  useEffect(() => {
    if (activeFolder.kind === 'folder') {
      const parts = activeFolder.name.split('/');
      if (parts.length > 1) {
        setExpandedFolders(prev => {
          const next = new Set(prev);
          for (let i = 1; i < parts.length; i++) next.add(parts.slice(0, i).join('/'));
          return next;
        });
      }
    }
  }, [activeFolder]);

  // Close context menu on scroll
  useEffect(() => {
    const handler = () => setCtxMenu(null);
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, []);

  // ── Navigation ────────────────────────────────────────────────────────────

  const navigateTo = (v: FolderView) => {
    setActiveFolder(v);
    setSelectedIds(new Set());
    lastSelectedIdRef.current = null;
  };

  const navigateToFolder = (path: string) => navigateTo({ kind: 'folder', name: path });

  const breadcrumbParts = useMemo(() => {
    if (activeFolder.kind === 'all') return [];
    return activeFolder.name.split('/');
  }, [activeFolder]);

  // ── Upload ────────────────────────────────────────────────────────────────

  const handleFiles = async (files: FileList | null, targetFolder?: string) => {
    if (!files || files.length === 0) return;
    const folder = targetFolder !== undefined ? targetFolder : defaultUploadFolder;

    // Pre-validate all files before uploading
    const rejected: string[] = [];
    const accepted: Array<{ file: File; ft: string }> = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ft = getFileType(file);
      if (!ft) rejected.push(file.name);
      else accepted.push({ file, ft });
    }
    if (rejected.length > 0) {
      const ext = rejected.map(n => n.slice(n.lastIndexOf('.')).toLowerCase() || n).join(', ');
      setError(`Unsupported file type${rejected.length > 1 ? 's' : ''}: ${ext}. See supported formats below.`);
      if (accepted.length === 0) return;
    } else {
      setError(null);
    }

    setUploading(true);
    for (let i = 0; i < accepted.length; i++) {
      const { file, ft } = accepted[i];
      setUploadProgress(`Uploading ${file.name} (${i + 1}/${accepted.length})…`);
      try {
        await api.uploadDocument({ name: file.name, file_type: ft, file, folder: folder || undefined });
      } catch (err: any) { setError(err.message ?? `Failed to upload ${file.name}`); }
    }
    setUploading(false); setUploadProgress(null);
    fetchDocuments();
  };

  // ── Selection ─────────────────────────────────────────────────────────────

  const clearSelection = () => { setSelectedIds(new Set()); lastSelectedIdRef.current = null; };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      const next = new Set(selectedIds); sortedDocs.forEach(d => next.delete(d.id)); setSelectedIds(next);
    } else {
      const next = new Set(selectedIds); sortedDocs.forEach(d => next.add(d.id)); setSelectedIds(next);
    }
  };

  const toggleSelectOne = (id: string, shiftKey: boolean) => {
    const next = new Set(selectedIds);
    if (shiftKey && lastSelectedIdRef.current) {
      const ids = sortedDocs.map(d => d.id);
      const fi = ids.indexOf(lastSelectedIdRef.current), ti = ids.indexOf(id);
      if (fi >= 0 && ti >= 0) {
        const [lo, hi] = fi < ti ? [fi, ti] : [ti, fi];
        for (let i = lo; i <= hi; i++) next.add(ids[i]);
        setSelectedIds(next); return;
      }
    }
    if (next.has(id)) next.delete(id); else next.add(id);
    lastSelectedIdRef.current = id;
    setSelectedIds(next);
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  // ── Drag & drop ───────────────────────────────────────────────────────────

  const handleRowDragStart = (doc: Document, e: React.DragEvent) => {
    draggingIdsRef.current = selectedIds.has(doc.id) ? Array.from(selectedIds) : [doc.id];
    e.dataTransfer.effectAllowed = 'move';
  };

  const doTreeDragOver = (path: string, e: React.DragEvent) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(path);
  };
  const doTreeDragLeave = (path: string, e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverFolder(prev => prev === path ? null : prev);
  };
  const doTreeDrop = async (path: string, e: React.DragEvent) => {
    e.preventDefault(); setDragOverFolder(null);
    const ids = draggingIdsRef.current; draggingIdsRef.current = [];
    if (!ids.length) return;
    try {
      for (const id of ids) await api.updateDocumentFolder(id, path || null);
      clearSelection(); fetchDocuments();
    } catch (err: any) { setError(err.message ?? 'Failed to move'); }
  };

  // ── Context menu ──────────────────────────────────────────────────────────

  const openCtxMenu = (e: React.MouseEvent, kind: CtxMenu['kind'], doc?: Document, folderPath?: string) => {
    e.preventDefault();
    // Right-clicking an unselected file clears selection and selects only that file
    if (kind === 'file' && doc && !selectedIds.has(doc.id)) {
      setSelectedIds(new Set([doc.id]));
      lastSelectedIdRef.current = doc.id;
    }
    setCtxMenu({ x: e.clientX, y: e.clientY, kind, doc, folderPath });
  };

  const closeCtx = () => setCtxMenu(null);

  // ── New folder ────────────────────────────────────────────────────────────

  const handleConfirmNewFolder = () => {
    const path = normalizeFolderPath(newFolderValue);
    if (path) {
      if (!pendingFolders.includes(path)) setPendingFolders(prev => [...prev, path]);
      navigateToFolder(path);
    }
    setShowNewFolderInput(false); setNewFolderValue('');
  };

  // ── Rename folder ─────────────────────────────────────────────────────────

  const openRenameFolder = (folderPath: string) => {
    const lastSeg = folderPath.split('/').pop() ?? folderPath;
    setRenamingFolder(folderPath);
    setRenameFolderValue(lastSeg);
    closeCtx();
  };

  const handleRenameFolder = async () => {
    if (!renamingFolder) return;
    const newSeg = normalizeFolderPath(renameFolderValue);
    if (!newSeg) return;
    const oldParts = renamingFolder.split('/');
    oldParts[oldParts.length - 1] = newSeg;
    const newPath = oldParts.join('/');
    if (newPath === renamingFolder) { setRenamingFolder(null); return; }

    setRenamingFolderBusy(true);
    try {
      const affected = documents.filter(d =>
        d.folder === renamingFolder || d.folder?.startsWith(renamingFolder + '/')
      );
      for (const doc of affected) {
        const newFolder = doc.folder!.replace(renamingFolder, newPath);
        await api.updateDocumentFolder(doc.id, newFolder);
      }
      setPendingFolders(prev =>
        prev.map(p => p === renamingFolder ? newPath : p.startsWith(renamingFolder + '/') ? newPath + p.slice(renamingFolder.length) : p)
      );
      if (activeFolder.kind === 'folder' && (activeFolder.name === renamingFolder || activeFolder.name.startsWith(renamingFolder + '/'))) {
        navigateToFolder(activeFolder.name.replace(renamingFolder, newPath));
      }
      setRenamingFolder(null);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message ?? 'Failed to rename folder');
    } finally {
      setRenamingFolderBusy(false);
    }
  };

  // ── Remove folder ─────────────────────────────────────────────────────────

  const handleRemoveFolder = async () => {
    if (!removingFolderPath) return;
    const parentPath = removingFolderPath.includes('/')
      ? removingFolderPath.split('/').slice(0, -1).join('/')
      : null;

    setRemovingFolderBusy(true);
    try {
      const affected = documents.filter(d =>
        d.folder === removingFolderPath || d.folder?.startsWith(removingFolderPath + '/')
      );
      for (const doc of affected) {
        let newFolder: string | null;
        if (doc.folder === removingFolderPath) {
          newFolder = parentPath;
        } else {
          const rel = doc.folder!.slice(removingFolderPath.length + 1);
          newFolder = parentPath ? `${parentPath}/${rel}` : rel;
        }
        await api.updateDocumentFolder(doc.id, newFolder);
      }
      setPendingFolders(prev => prev.filter(p => p !== removingFolderPath && !p.startsWith(removingFolderPath + '/')));
      if (activeFolder.kind === 'folder' && (activeFolder.name === removingFolderPath || activeFolder.name.startsWith(removingFolderPath + '/'))) {
        navigateTo(parentPath ? { kind: 'folder', name: parentPath } : { kind: 'all' });
      }
      setRemovingFolderPath(null);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message ?? 'Failed to remove folder');
    } finally {
      setRemovingFolderBusy(false);
    }
  };

  // ── Move ──────────────────────────────────────────────────────────────────

  const handleMoveConfirm = async () => {
    setMovingDocs(true);
    try {
      for (const doc of selectedDocs) await api.updateDocumentFolder(doc.id, moveTarget || null);
      setShowMoveDialog(false); clearSelection(); fetchDocuments();
    } catch (err: any) { setError(err.message ?? 'Failed to move'); }
    finally { setMovingDocs(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      for (const doc of deletingDocs) await api.deleteDocument(doc.id);
      setSelectedIds(prev => { const next = new Set(prev); deletingDocs.forEach(d => next.delete(d.id)); return next; });
      setDeletingDocs([]); fetchDocuments();
    } catch (err: any) { setError(err.message ?? 'Failed to delete'); }
    finally { setDeleting(false); }
  };

  // ── Version history ───────────────────────────────────────────────────────

  const handleViewVersions = async (doc: Document) => {
    setShowVersionHistory(doc); setLoadingVersions(true);
    try { setVersions(await api.listDocumentVersions(doc.id)); }
    catch { setVersions([]); } finally { setLoadingVersions(false); }
  };

  const handleRestoreVersion = async (vId: string) => {
    if (!showVersionHistory) return;
    setRestoringVersion(vId);
    try { await api.restoreDocumentVersion(showVersionHistory.id, vId); setShowVersionHistory(null); fetchDocuments(); }
    catch (err: any) { setError(err.message ?? 'Failed to restore'); }
    finally { setRestoringVersion(null); }
  };

  // ── Retention ─────────────────────────────────────────────────────────────

  const handleSaveRetention = async () => {
    if (!showRetentionDialog) return;
    setSavingRetention(true);
    try {
      await api.setDocumentRetention(showRetentionDialog.id, retentionValue === '' ? null : parseInt(retentionValue, 10));
      setShowRetentionDialog(null); fetchDocuments();
    } catch (err: any) { setError(err.message ?? 'Failed to save retention'); }
    finally { setSavingRetention(false); }
  };

  // ── New version ───────────────────────────────────────────────────────────

  const handleNewVersionFile = async (doc: Document, files: FileList | null) => {
    if (!files?.length) return;
    const ft = getFileType(files[0]);
    if (!ft) { setError(`Unsupported type: ${files[0].name}`); return; }
    setUploading(true);
    try {
      await api.uploadDocumentVersion(doc.id, { file_type: ft, file: files[0] });
      fetchDocuments();
    } catch (err: any) { setError(err.message ?? 'Failed to upload version'); }
    finally { setUploading(false); }
  };

  const pickVersionFile = (doc: Document) => {
    const inp = window.document.createElement('input');
    inp.type = 'file'; inp.accept = ACCEPT_ATTR;
    inp.onchange = e => handleNewVersionFile(doc, (e.target as HTMLInputElement).files);
    inp.click();
  };

  // ── Tree ctx ──────────────────────────────────────────────────────────────

  const treeCtx: TreeCtx = {
    classes,
    activeFolder,
    onSelect: navigateTo,
    expandedFolders,
    toggleExpand: (path: string) =>
      setExpandedFolders(prev => { const next = new Set(prev); if (next.has(path)) next.delete(path); else next.add(path); return next; }),
    dragOverFolder,
    onDragOver: doTreeDragOver,
    onDragLeave: doTreeDragLeave,
    onDrop: doTreeDrop,
    onContextMenu: (e, folderPath) => openCtxMenu(e, 'folder', undefined, folderPath),
  };

  // ── Context menu actions wiring ───────────────────────────────────────────

  const ctxDoc = ctxMenu?.doc ?? (ctxMenu?.kind === 'file' && selectedDocs.length === 1 ? selectedDocs[0] : undefined);
  const ctxActionDocs = selectedDocs.length > 0 ? selectedDocs : ctxDoc ? [ctxDoc] : [];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box className={classes.root} onContextMenu={isAdmin ? e => { if (e.target === tableAreaRef.current) openCtxMenu(e, 'bg'); } : undefined}>

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <Box className={classes.sidebar}>
        <Box className={classes.sidebarHeader}>
          <Typography className={classes.sidebarTitle}>Folders</Typography>
          {isAdmin && (
            <Tooltip title="New folder">
              <IconButton size="small" style={{ padding: 3 }} onClick={() => { setShowNewFolderInput(v => !v); setNewFolderValue(''); }}>
                <CreateNewFolderIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {showNewFolderInput && (
          <Box className={classes.sidebarNewInput}>
            <TextField
              autoFocus size="small" placeholder="path/to/folder"
              value={newFolderValue} onChange={e => setNewFolderValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleConfirmNewFolder(); if (e.key === 'Escape') { setShowNewFolderInput(false); setNewFolderValue(''); } }}
              fullWidth helperText="Use / for nesting"
              InputProps={{
                style: { fontSize: 12 },
                endAdornment: (
                  <Box display="flex">
                    <IconButton size="small" onClick={handleConfirmNewFolder} style={{ padding: 3 }}><CheckIcon style={{ fontSize: 14 }} /></IconButton>
                    <IconButton size="small" style={{ padding: 3 }} onClick={() => { setShowNewFolderInput(false); setNewFolderValue(''); }}><CloseIcon style={{ fontSize: 14 }} /></IconButton>
                  </Box>
                ),
              }}
            />
          </Box>
        )}

        {/* All Documents */}
        <Box
          className={[classes.navEntry, activeFolder.kind === 'all' ? classes.navEntryActive : ''].join(' ')}
          onClick={() => navigateTo({ kind: 'all' })}
        >
          <Box className={classes.expandArrow} />
          <LayersIcon className={classes.navIcon} />
          <span className={classes.navLabel}>All Documents</span>
          <span className={classes.navCount}>{documents.length}</span>
        </Box>

        {/* Unfiled drop target */}
        {documents.some(d => !d.folder) && (
          <Box
            className={[classes.navEntry, dragOverFolder === '__unfiled__' ? classes.navEntryDrop : ''].join(' ')}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder('__unfiled__'); }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverFolder(null); }}
            onDrop={async e => {
              e.preventDefault(); setDragOverFolder(null);
              const ids = draggingIdsRef.current; draggingIdsRef.current = [];
              if (!ids.length) return;
              try { for (const id of ids) await api.updateDocumentFolder(id, null); clearSelection(); fetchDocuments(); }
              catch (err: any) { setError(err.message ?? 'Failed to move'); }
            }}
            onClick={() => navigateTo({ kind: 'all' })}
          >
            <Box className={classes.expandArrow} />
            <InboxIcon className={classes.navIcon} />
            <span className={classes.navLabel} style={{ opacity: 0.65 }}>Unfiled</span>
            <span className={classes.navCount}>{documents.filter(d => !d.folder).length}</span>
          </Box>
        )}

        {folderTree.length > 0 && <Divider style={{ margin: '4px 0' }} />}
        {folderTree.map(n => <FolderTreeNode key={n.path} node={n} depth={0} ctx={treeCtx} />)}
      </Box>

      {/* ── Main panel ──────────────────────────────────────────────────── */}
      <Box className={classes.main}>

        {/* Toolbar / breadcrumb */}
        <Box className={classes.toolbar}>
          {/* Breadcrumb */}
          <Box className={classes.breadcrumb}>
            <span className={breadcrumbParts.length > 0 ? classes.breadcrumbLink : classes.breadcrumbCurrent}
              onClick={() => navigateTo({ kind: 'all' })}>
              All Documents
            </span>
            {breadcrumbParts.map((part, idx) => {
              const path = breadcrumbParts.slice(0, idx + 1).join('/');
              const isLast = idx === breadcrumbParts.length - 1;
              return (
                <Box key={path} display="flex" alignItems="center">
                  <NavigateNextIcon className={classes.breadcrumbSep} />
                  <span
                    className={isLast ? classes.breadcrumbCurrent : classes.breadcrumbLink}
                    onClick={isLast ? undefined : () => navigateToFolder(path)}
                  >
                    {part}
                  </span>
                </Box>
              );
            })}
          </Box>

          <Box flex={1} />

          {error && <Typography className={classes.error}>{error}</Typography>}

          {isAdmin && (
            <>
              <Tooltip title={`New folder inside ${activeFolder.kind === 'folder' ? activeFolder.name : 'root'}`}>
                <IconButton size="small"
                  onClick={() => {
                    const prefix = activeFolder.kind === 'folder' ? activeFolder.name + '/' : '';
                    setNewFolderValue(prefix);
                    setShowNewFolderInput(true);
                  }}
                >
                  <CreateNewFolderIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Button variant="outlined" size="small" startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                Upload
              </Button>
            </>
          )}
          <input ref={fileInputRef} type="file" multiple accept={ACCEPT_ATTR} style={{ display: 'none' }}
            onChange={e => handleFiles(e.target.files)} />
        </Box>

        {/* Supported formats info */}
        {isAdmin && (
          <Box className={classes.supportedFormats}>
            <Typography variant="caption" color="textSecondary">
              <strong>Supported formats:</strong> PDF, Word (docx), PowerPoint (pptx, ppt), Excel (xlsx, xls), ODF (odt, odp, ods), RTF, CSV, Text (txt, md, sql, xml, json, html, yaml, toml, ini, log)
            </Typography>
          </Box>
        )}

        {uploading && (
          <Box className={classes.uploadProgress}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary">{uploadProgress}</Typography>
          </Box>
        )}

        {loading && <Box className={classes.emptyState}><CircularProgress size={28} /></Box>}

        {/* Main content: folders + files */}
        {!loading && (
          <div
            ref={tableAreaRef}
            className={classes.tableScroll}
            onContextMenu={isAdmin ? e => { if (e.target === tableAreaRef.current || (e.target as HTMLElement).closest('table') === null) openCtxMenu(e, 'bg'); } : undefined}
            onDragOver={isAdmin ? e => e.preventDefault() : undefined}
            onDrop={isAdmin ? e => { if (e.dataTransfer.files?.length) { e.preventDefault(); handleFiles(e.dataTransfer.files); } } : undefined}
          >
            {currentSubFolderPaths.length === 0 && sortedDocs.length === 0 ? (
              <Box className={classes.emptyState}
                onDragOver={isAdmin ? e => e.preventDefault() : undefined}
                onDrop={isAdmin ? e => { e.preventDefault(); if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files); } : undefined}
              >
                <CloudUploadIcon style={{ fontSize: 44, opacity: 0.2, display: 'block', margin: '0 auto 12px' }} />
                <Typography variant="body2">
                  {activeFolder.kind === 'all'
                    ? 'No documents yet. Drag & drop files here or click Upload.'
                    : 'Empty folder. Drop files here, or drag documents from another folder.'}
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allVisibleSelected} indeterminate={someVisibleSelected} onChange={toggleSelectAll} />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortKey === 'name'} direction={sortKey === 'name' ? sortDir : 'asc'} onClick={() => toggleSort('name')}>
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortKey === 'type'} direction={sortKey === 'type' ? sortDir : 'asc'} onClick={() => toggleSort('type')}>
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={sortKey === 'date'} direction={sortKey === 'date' ? sortDir : 'asc'} onClick={() => toggleSort('date')}>
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" style={{ width: 110 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* ── Folder rows ── */}
                  {currentSubFolderPaths.map(folderPath => {
                    const displayName = activeFolder.kind === 'folder'
                      ? folderPath.slice(activeFolder.name.length + 1)
                      : folderPath;
                    const count = folderTotalCounts[folderPath] ?? 0;
                    const isDrop = dragOverFolder === folderPath;
                    return (
                      <TableRow
                        key={folderPath}
                        className={[classes.folderRow, isDrop ? classes.folderRowDrop : ''].join(' ')}
                        onDoubleClick={() => navigateToFolder(folderPath)}
                        onContextMenu={e => openCtxMenu(e, 'folder', undefined, folderPath)}
                        onDragOver={e => doTreeDragOver(folderPath, e)}
                        onDragLeave={e => doTreeDragLeave(folderPath, e)}
                        onDrop={e => doTreeDrop(folderPath, e)}
                      >
                        <TableCell padding="checkbox" />
                        <TableCell>
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <FolderIcon style={{ fontSize: 18, color: '#ffa000', flexShrink: 0 }} />
                            <span className={classes.folderName}>{displayName}</span>
                            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                              {count} item{count !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell align="right">
                          <Box className={classes.folderRowActions} display="flex" justifyContent="flex-end">
                            {isAdmin && (
                              <>
                                <Tooltip title="Rename folder">
                                  <IconButton size="small" onClick={e => { e.stopPropagation(); openRenameFolder(folderPath); }}>
                                    <DriveFileRenameOutlineIcon style={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Open folder">
                                  <IconButton size="small" onClick={() => navigateToFolder(folderPath)}>
                                    <FolderOpenIcon style={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Divider between folders and files */}
                  {currentSubFolderPaths.length > 0 && sortedDocs.length > 0 && (
                    <TableRow><TableCell colSpan={5} style={{ padding: 0 }}><Divider /></TableCell></TableRow>
                  )}

                  {/* ── File rows ── */}
                  {sortedDocs.map(doc => (
                    <TableRow
                      key={doc.id}
                      hover
                      selected={selectedIds.has(doc.id)}
                      draggable={isAdmin}
                      onDragStart={e => handleRowDragStart(doc, e)}
                      onDragEnd={() => { draggingIdsRef.current = []; }}
                      onContextMenu={e => openCtxMenu(e, 'file', doc)}
                      style={{ cursor: isAdmin ? 'context-menu' : 'default' }}
                    >
                      <TableCell padding="checkbox"
                        onClick={e => toggleSelectOne(doc.id, (e.nativeEvent as MouseEvent).shiftKey)}>
                        <Checkbox size="small" checked={selectedIds.has(doc.id)} onChange={() => {}} />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <InsertDriveFileIcon style={{ fontSize: 16, flexShrink: 0, opacity: 0.5 }} />
                          <span className={classes.fileName}>{doc.name}</span>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={doc.file_type.toUpperCase()} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{new Date(doc.created_at).toLocaleDateString()}</Typography>
                      </TableCell>
                      <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={async () => { const url = await api.getDocumentContentUrl(doc.id); window.open(url, '_blank'); }}>
                            <GetAppIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Version history">
                          <IconButton size="small" onClick={() => handleViewVersions(doc)}>
                            <HistoryIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {isAdmin && (
                          <>
                            <Tooltip title="Upload new version">
                              <IconButton size="small" onClick={() => pickVersionFile(doc)}>
                                <PublishIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Retention">
                              <IconButton size="small" onClick={() => { setShowRetentionDialog(doc); setRetentionValue(doc.max_versions != null ? String(doc.max_versions) : ''); }}>
                                <SettingsIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => setDeletingDocs([doc])}>
                                <DeleteIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <Box className={classes.bulkBar}>
            <Typography className={classes.bulkCount}>{selectedIds.size} selected</Typography>
            <Button size="small" variant="text" style={{ fontSize: 11, textTransform: 'none' }} onClick={toggleSelectAll}>
              {allVisibleSelected ? 'Deselect all' : 'Select all visible'}
            </Button>
            <Box flex={1} />
            {isAdmin && (
              <>
                <Button size="small" variant="outlined" color="primary" startIcon={<FolderIcon />}
                  onClick={() => { setMoveTarget(''); setShowMoveDialog(true); }}>
                  Move to folder
                </Button>
                <Button size="small" variant="outlined" color="secondary" startIcon={<DeleteIcon />}
                  onClick={() => setDeletingDocs(selectedDocs)}>
                  Delete
                </Button>
              </>
            )}
            <Tooltip title="Clear selection">
              <IconButton size="small" onClick={clearSelection}><CloseIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* ── Context Menu ────────────────────────────────────────────────── */}
      <Menu
        open={Boolean(ctxMenu)}
        onClose={closeCtx}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
      >
        {ctxMenu?.kind === 'folder' && [
          <MenuItem key="open" onClick={() => { navigateToFolder(ctxMenu.folderPath!); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><FolderOpenIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>,
          isAdmin && <MenuItem key="rename" onClick={() => openRenameFolder(ctxMenu.folderPath!)}>
            <ListItemIcon className={classes.menuIcon}><DriveFileRenameOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rename folder…</ListItemText>
          </MenuItem>,
          isAdmin && <MenuItem key="newfolder" onClick={() => {
            setNewFolderValue(ctxMenu.folderPath! + '/');
            setShowNewFolderInput(true);
            closeCtx();
          }}>
            <ListItemIcon className={classes.menuIcon}><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
            <ListItemText>New subfolder…</ListItemText>
          </MenuItem>,
          <Divider key="d1" />,
          isAdmin && <MenuItem key="remove" onClick={() => { setRemovingFolderPath(ctxMenu.folderPath!); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><DeleteIcon fontSize="small" style={{ color: '#f44336' }} /></ListItemIcon>
            <ListItemText><span style={{ color: '#f44336' }}>Remove folder</span></ListItemText>
          </MenuItem>,
        ].filter(Boolean)}

        {ctxMenu?.kind === 'file' && [
          <MenuItem key="dl" onClick={async () => { const url = await api.getDocumentContentUrl(ctxActionDocs[0].id); window.open(url, '_blank'); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><GetAppIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>,
          <MenuItem key="hist" onClick={() => { handleViewVersions(ctxActionDocs[0]); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Version history</ListItemText>
          </MenuItem>,
          isAdmin && <Divider key="d1" />,
          isAdmin && <MenuItem key="move" onClick={() => { setMoveTarget(ctxActionDocs[0].folder ?? ''); setShowMoveDialog(true); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><FolderSpecialIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Move to folder…{ctxActionDocs.length > 1 ? ` (${ctxActionDocs.length} files)` : ''}</ListItemText>
          </MenuItem>,
          isAdmin && <MenuItem key="newver" onClick={() => { pickVersionFile(ctxActionDocs[0]); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><PublishIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Upload new version</ListItemText>
          </MenuItem>,
          isAdmin && <MenuItem key="ret" onClick={() => { setShowRetentionDialog(ctxActionDocs[0]); setRetentionValue(ctxActionDocs[0].max_versions != null ? String(ctxActionDocs[0].max_versions) : ''); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Retention settings</ListItemText>
          </MenuItem>,
          isAdmin && <Divider key="d2" />,
          isAdmin && <MenuItem key="del" onClick={() => { setDeletingDocs(ctxActionDocs); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><DeleteIcon fontSize="small" style={{ color: '#f44336' }} /></ListItemIcon>
            <ListItemText><span style={{ color: '#f44336' }}>Delete{ctxActionDocs.length > 1 ? ` (${ctxActionDocs.length} files)` : ''}</span></ListItemText>
          </MenuItem>,
        ].filter(Boolean)}

        {ctxMenu?.kind === 'bg' && isAdmin && [
          <MenuItem key="nf" onClick={() => { setShowNewFolderInput(true); setNewFolderValue(defaultUploadFolder ? defaultUploadFolder + '/' : ''); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
            <ListItemText>New folder…</ListItemText>
          </MenuItem>,
          <MenuItem key="ul" onClick={() => { fileInputRef.current?.click(); closeCtx(); }}>
            <ListItemIcon className={classes.menuIcon}><CloudUploadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Upload files…</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* ── Dialogs ─────────────────────────────────────────────────────── */}

      {/* Rename folder */}
      <Dialog open={Boolean(renamingFolder)} onClose={() => setRenamingFolder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Folder</DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
            Current path: <strong>{renamingFolder}</strong>
          </Typography>
          <TextField
            autoFocus label="New name" fullWidth
            value={renameFolderValue} onChange={e => setRenameFolderValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(); }}
            disabled={renamingFolderBusy}
            helperText="Renames the last segment. All documents inside will be updated."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingFolder(null)} disabled={renamingFolderBusy}>Cancel</Button>
          <Button onClick={handleRenameFolder} color="primary" variant="contained" disabled={renamingFolderBusy}>
            {renamingFolderBusy ? <CircularProgress size={20} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove folder */}
      <Dialog open={Boolean(removingFolderPath)} onClose={() => setRemovingFolderPath(null)}>
        <DialogTitle>Remove Folder</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove folder <strong>{removingFolderPath}</strong>? All documents inside will be moved to the parent folder. The documents themselves will not be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingFolderPath(null)} disabled={removingFolderBusy}>Cancel</Button>
          <Button onClick={handleRemoveFolder} color="secondary" disabled={removingFolderBusy}>
            {removingFolderBusy ? <CircularProgress size={20} /> : 'Remove folder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk move */}
      <Dialog open={showMoveDialog} onClose={() => setShowMoveDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move {selectedIds.size || 1} document{(selectedIds.size || 1) !== 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus label="Destination folder" placeholder="e.g. hr/policies/2025"
            fullWidth value={moveTarget} onChange={e => setMoveTarget(e.target.value)}
            disabled={movingDocs} helperText="Use / for nesting. Leave empty to remove from folder."
            style={{ marginBottom: 12 }}
          />
          {flatFolderPaths.length > 0 && (
            <Box>
              <Typography variant="caption" color="textSecondary">Existing folders:</Typography>
              <Box display="flex" flexWrap="wrap" style={{ marginTop: 6 }}>
                {flatFolderPaths.map(f => (
                  <Chip key={f} label={f} size="small"
                    variant={moveTarget === f ? 'default' : 'outlined'}
                    color={moveTarget === f ? 'primary' : 'default'}
                    clickable className={classes.moveChip}
                    onClick={() => setMoveTarget(f)}
                    icon={<FolderIcon style={{ fontSize: 14 }} />}
                  />
                ))}
                <Chip label="Remove from folder" size="small"
                  variant={moveTarget === '' ? 'default' : 'outlined'}
                  clickable className={classes.moveChip}
                  onClick={() => setMoveTarget('')}
                  icon={<InboxIcon style={{ fontSize: 14 }} />}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMoveDialog(false)} disabled={movingDocs}>Cancel</Button>
          <Button onClick={handleMoveConfirm} color="primary" variant="contained" disabled={movingDocs}>
            {movingDocs ? <CircularProgress size={20} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version history */}
      <Dialog open={Boolean(showVersionHistory)} onClose={() => setShowVersionHistory(null)} maxWidth="md" fullWidth>
        <DialogTitle>Version History — {showVersionHistory?.name}</DialogTitle>
        <DialogContent>
          {loadingVersions && <CircularProgress size={24} />}
          {!loadingVersions && versions.length === 0 && <Typography color="textSecondary">No versions found.</Typography>}
          {!loadingVersions && versions.length > 0 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Version</TableCell><TableCell>Created</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>
                {versions.map((v, idx) => {
                  const isLatest = idx === versions.length - 1;
                  return (
                    <TableRow key={v.id}>
                      <TableCell>v{v.version_number}</TableCell>
                      <TableCell>{new Date(v.created_at).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {!isLatest && isAdmin && (
                          <Tooltip title="Restore">
                            <IconButton size="small" onClick={() => handleRestoreVersion(v.id)} disabled={restoringVersion === v.id}>
                              {restoringVersion === v.id ? <CircularProgress size={16} /> : <RestoreIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {isLatest && <Typography variant="caption" color="textSecondary">Current</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setShowVersionHistory(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Retention */}
      <Dialog open={Boolean(showRetentionDialog)} onClose={() => setShowRetentionDialog(null)}>
        <DialogTitle>Version Retention — {showRetentionDialog?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>Max versions to retain. Leave empty for unlimited.</DialogContentText>
          <TextField autoFocus margin="dense" label="Max Versions" type="number" fullWidth
            value={retentionValue} onChange={e => setRetentionValue(e.target.value)}
            inputProps={{ min: 1 }} disabled={savingRetention} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRetentionDialog(null)} disabled={savingRetention}>Cancel</Button>
          <Button onClick={handleSaveRetention} color="primary" variant="contained" disabled={savingRetention}>
            {savingRetention ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={deletingDocs.length > 0} onClose={() => setDeletingDocs([])}>
        <DialogTitle>Delete {deletingDocs.length > 1 ? `${deletingDocs.length} Documents` : 'Document'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deletingDocs.length === 1
              ? <>Delete <strong>{deletingDocs[0]?.name}</strong>? This removes all chunks and agent associations.</>
              : <>Delete {deletingDocs.length} documents? This removes all their chunks and agent associations.</>
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingDocs([])} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="secondary" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
